import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Trophy, Award, Crown, Users, Check, Rocket, Bot, BrainCircuit, Star } from "lucide-react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import NonprofitImpactCalculator from "@/components/calculators/NonprofitImpactCalculator";

export default function TechGrantPage() {
  const navigate = useNavigate();

  const qualificationSteps = [
    {
      icon: Rocket,
      title: "Create Your First Campaign",
      description: "Set up your nonprofit profile and create an engaging campaign."
    },
    {
      icon: Bot,
      title: "Add AI Assistant to Your Site",
      description: "Install our AI chatbot to engage supporters 24/7."
    },
    {
      icon: BrainCircuit,
      title: "Launch Your Impact Hub",
      description: "Create an interactive page to showcase your opportunities."
    },
    {
      icon: Star,
      title: "Join Our Community",
      description: "Connect with other nonprofits and share best practices."
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8fff0]">
      <SEO title="Tech Grant Program | CauseConnect" description="Empowering nonprofits with AI-powered tools to maximize their impact." />

      {/* Hero Section */}
      <section className="py-16 md:py-24 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            CauseConnect Tech Grant Program
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Empowering nonprofits with AI-driven automation, impact reporting, and engagement tools.
          </p>
          <Button className="bg-[#2E7D32] hover:bg-[#246537] text-white px-8 py-6 h-auto text-lg" onClick={() => navigate("/signup")}>
            Apply Now
          </Button>
        </motion.div>
      </section>

      {/* Impact Calculator */}
      <section className="max-w-6xl mx-auto mb-16">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Calculate Your Nonprofit's Impact</h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Use our AI-powered calculator to estimate time savings and efficiency gains.
          </p>
        </div>
        <NonprofitImpactCalculator />
      </section>

      {/* Grant Giveaways */}
      <section className="max-w-5xl mx-auto mb-16 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Win AI-Powered Nonprofit Plans 🚀</h2>
        <p className="text-lg text-gray-600 mb-8">100 Winners Every Week. 1 Grand Winner Every Month!</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: Trophy, title: "Base", prize: "100 Essential Plan", value: "$49" },
            { icon: Award, title: "Premium", prize: "10 Pro Plan Grants", value: "$99" },
            { icon: Crown, title: "Monthly Grand", prize: "6 Months Pro Plan + $500 Tech Credit", value: "$1,250" },
            { icon: Users, title: "Community", prize: "Access to Nonprofit Tech Community", value: "All Winners" }
          ].map((item, index) => (
            <Card key={index} className="p-6 text-center bg-white shadow-lg hover:shadow-xl transition-all">
              <div className="mb-4">
                <item.icon className="h-12 w-12 text-[#2E7D32] mx-auto" />
              </div>
              <h3 className="text-xl font-bold mb-2">{item.title}</h3>
              <p className="text-gray-600">{item.prize}</p>
              <div className="text-[#2E7D32] font-bold mt-2">{item.value}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* Steps to Qualify */}
      <section className="max-w-5xl mx-auto mb-16 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">4 Steps to Qualify 🚀</h2>
        <p className="text-lg text-gray-600 mb-8">Complete these steps to enter the weekly draw.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {qualificationSteps.map((step, index) => (
            <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#2E7D32]/10 flex items-center justify-center">
                  <step.icon className="w-6 h-6 text-[#2E7D32]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Eligibility & Benefits */}
      <section className="max-w-4xl mx-auto mb-16">
        <div className="grid md:grid-cols-2 gap-12">
          {[
            { title: "Eligibility Criteria", items: ["Registered 501(c)(3) or Indian nonprofit", "Active website and social media presence", "Commitment to using technology for social impact", "Clear mission and community impact"] },
            { title: "Grant Benefits", items: ["Full access to AI-powered tools", "Advanced analytics & impact tracking", "Priority support & training", "Access to nonprofit tech community"] }
          ].map((section, index) => (
            <motion.div key={index} initial={{ opacity: 0, x: index === 0 ? -20 : 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl font-bold mb-6">{section.title}</h2>
              <ul className="space-y-4">
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#2E7D32]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="text-center mt-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl font-bold mb-6">Ready to Transform Your Impact?</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join hundreds of nonprofits already leveraging AI-powered solutions.
          </p>
          <div className="text-center mt-8">
          <Button className="bg-[#2E7D32] hover:bg-[#246537] text-white px-8 py-5 h-auto text-lg" onClick={() => navigate("/signup")}>
            Apply Now
          </Button>
          </div>
        </motion.div>
        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            
          </p>
      </section>
    </div>
  );
}
